import axios from 'axios';
import { CORE_API } from '../config';

type CoreProviderConfig = {
  provider: {
    id: number;
    email: string;
  };
  scheduling: {
    view_recording: boolean;
  };
};

type ProviderConfig = {
  provider: {
    id: number;
    email: string;
  };
  scheduling: {
    viewRecording: boolean;
  };
};

type SignInProps = {
  phone: string;
  password: string;
};

type ResetPasswordProps = {
  email: string;
};

type SetPasswordProps = {
  password: string;
  password_confirmation: string;
  auth_token: string;
  // Core requires a staff user's current password to change it (EH-1708). In the
  // reset flow that is the emailed code, which core stores as the password.
  current_password: string;
};

export type Role = {
  id: number | string;
  name: string;
};

type SetAuthProps = {
  auth_token: string;
  user_id: number;
  roles?: Role[];
};

export type AuthProps = {
  Authorization: {
    id: number;
    token: string;
  };
};

export const AUTHORIZATION = 'Authorization';
// Kept apart from `Authorization`, so switching user (which overwrites it)
// keeps the original session's deadline and Hub token.
export const AUTHORIZATION_EXPIRES_AT = 'AuthorizationExpiresAt';
// The Core token a Hub sign-in issued: the only one sign-out ends in Core, and
// what marks a session as a Hub one.
export const AUTHORIZATION_HUB_TOKEN = 'AuthorizationHubToken';
// No longer written. Still cleared for one release, for browsers that stored it.
const STALE_AUTHORIZATION_SOURCE = 'AuthorizationSource';

const AuthService = {
  signIn(payload: SignInProps) {
    return axios.post(`${CORE_API.url}/v1/auth`, payload);
  },

  resetPassword(payload: ResetPasswordProps) {
    return axios.post(`${CORE_API.url}/v1/auth/send_reset_email`, payload);
  },

  setPassword(payload: SetPasswordProps) {
    return axios.post(`${CORE_API.url}/v1/profile/change_password`, payload);
  },

  // Auth Data
  removeAuth() {
    localStorage.removeItem(AUTHORIZATION);
    AuthService.clearSessionMarkers();
  },

  // Only signs out `token`'s session, so a stale tab cannot wipe a newer sign-in.
  removeAuthIfToken(token: string | undefined) {
    if (token && AuthService.getAuth()?.Authorization.token === token) {
      AuthService.removeAuth();
    }
  },

  setAuth(payload: SetAuthProps) {
    localStorage.setItem(
      AUTHORIZATION,
      JSON.stringify({
        id: payload.user_id,
        auth_token: payload.auth_token,
        ...(payload.roles ? { roles: payload.roles } : {}),
      })
    );
  },

  /** When the stored session ends, as an ISO date, if it is known. */
  getExpiresAt(): string | null {
    return localStorage.getItem(AUTHORIZATION_EXPIRES_AT);
  },

  setExpiresAt(expiresAt: string) {
    localStorage.setItem(AUTHORIZATION_EXPIRES_AT, expiresAt);
  },

  /** The Core token the Hub sign-in issued, if this session came from one. */
  getHubToken(): string | null {
    return localStorage.getItem(AUTHORIZATION_HUB_TOKEN) || null;
  },

  setHubToken(token: string) {
    localStorage.setItem(AUTHORIZATION_HUB_TOKEN, token);
  },

  clearSessionMarkers() {
    localStorage.removeItem(STALE_AUTHORIZATION_SOURCE);
    localStorage.removeItem(AUTHORIZATION_EXPIRES_AT);
    localStorage.removeItem(AUTHORIZATION_HUB_TOKEN);
  },

  // Undefined unless a usable token is stored: a missing, malformed or
  // token-less entry is not a session.
  getAuth(): AuthProps | undefined {
    const stored = localStorage.getItem(AUTHORIZATION);

    if (stored === 'undefined') {
      localStorage.removeItem(AUTHORIZATION);
      return undefined;
    }

    let Authorization;
    try {
      Authorization = JSON.parse(stored || 'null');
    } catch (error) {
      return undefined;
    }

    if (typeof Authorization?.auth_token !== 'string' || !Authorization.auth_token) {
      return undefined;
    }

    return {
      Authorization: {
        id: Authorization.id,
        token: Authorization.auth_token,
      },
    };
  },

  async getConfig(): Promise<ProviderConfig | null> {
    const auth = AuthService.getAuth();
    const token = auth?.Authorization.token || '';

    try {
      const response = await axios.get<CoreProviderConfig>(
        `${CORE_API.url}/v4/providers/me/config`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { provider, scheduling } = response.data;

      return {
        provider: {
          id: provider.id,
          email: provider.email,
        },
        scheduling: {
          viewRecording: scheduling.view_recording,
        },
      };
    } catch (error) {
      // Unauthorized or empty token, we don't care about this
      return null;
    }
  },
};

export default AuthService;
